import React, { useState, useEffect } from 'react';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const BookingCalendar = ({ service, consultant, onDateTimeSelect, onBack }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date?.setDate(today?.getDate() + i);
      if (date?.getDay() !== 0 && date?.getDay() !== 6) {
        dates?.push({
          value: date?.toISOString()?.split('T')?.[0],
          label: date?.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }),
          short: date?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'numeric', year: 'numeric' }),
          date
        });
      }
    }
    return dates;
  };

  const generateTimeSlots = (date) => {
    const slots = [];
    const consultantHours = consultant?.available_hours || {};
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames?.[new Date(date)?.getDay()];
    const defaultHours = { start: '09:00', end: '17:00', break_start: '12:00', break_end: '13:00' };
    const dayHours = consultantHours?.[dayOfWeek] || defaultHours;

    if (!dayHours || dayHours?.closed) return slots;

    let currentTime = new Date(`1970-01-01T${dayHours?.start || '09:00'}:00`);
    const endDateTime = new Date(`1970-01-01T${dayHours?.end || '17:00'}:00`);
    const breakStart = dayHours?.break_start;
    const breakEnd = dayHours?.break_end;

    while (currentTime < endDateTime) {
      const timeString = currentTime?.toTimeString()?.slice(0, 5);
      const isInBreak = breakStart && breakEnd && timeString >= breakStart && timeString < breakEnd;
      if (!isInBreak) {
        slots?.push({
          value: timeString,
          label: currentTime?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false }),
          available: true
        });
      }
      currentTime?.setMinutes(currentTime?.getMinutes() + 30);
    }
    return slots;
  };

  const availableDates = generateAvailableDates();

  useEffect(() => {
    if (selectedDate) {
      setLoading(true);
      setTimeout(() => {
        setAvailableSlots(generateTimeSlots(selectedDate));
        setLoading(false);
      }, 300);
    } else {
      setAvailableSlots([]);
    }
  }, [selectedDate, consultant]);

  const handleConfirmDateTime = () => {
    if (selectedDate && selectedTime) {
      onDateTimeSelect?.(new Date(`${selectedDate}T${selectedTime}:00`)?.toISOString());
    }
  };

  const consultantName = consultant?.name || consultant?.notary_name || consultant?.user_profile?.full_name || 'Consultant';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="outline" size="sm" iconName="ArrowLeft" iconPosition="left" onClick={onBack}>
          Retour
        </Button>
        <div>
          <h2 className="text-2xl font-heading font-semibold text-foreground">
            Date & Heure
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {service?.name} avec {consultantName}
          </p>
        </div>
      </div>

      {/* Summary card */}
      <div className="bg-muted/40 border border-border rounded-lg px-5 py-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <p className="text-muted-foreground mb-0.5">Service</p>
            <p className="font-medium text-foreground">{service?.name}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Consultant</p>
            <p className="font-medium text-foreground">{consultantName}</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Durée</p>
            <p className="font-medium text-foreground">{service?.duration} min</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-0.5">Tarif</p>
            <p className="font-medium text-foreground">{service?.price}</p>
          </div>
        </div>
      </div>

      {/* Date + Time grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Date */}
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">Sélectionner une date</p>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
            {availableDates?.map((date) => (
              <button
                key={date?.value}
                onClick={() => { setSelectedDate(date?.value); setSelectedTime(''); }}
                className={cn(
                  'w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors',
                  selectedDate === date?.value
                    ? 'bg-primary text-primary-foreground font-medium'
                    : 'hover:bg-accent hover:text-accent-foreground text-foreground'
                )}
              >
                <span className="capitalize font-medium">{date?.label}</span>
                <span className={cn('block text-xs mt-0.5', selectedDate === date?.value ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                  {date?.short}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Time */}
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">Sélectionner une heure</p>
          </div>

          {!selectedDate ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Sélectionnez d'abord une date</span>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
            </div>
          ) : availableSlots?.length === 0 ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Aucun créneau disponible pour cette date</span>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-1.5 max-h-72 overflow-y-auto pr-1">
              {availableSlots?.map((slot) => (
                <button
                  key={slot?.value}
                  onClick={() => setSelectedTime(slot?.value)}
                  disabled={!slot?.available}
                  className={cn(
                    'py-2 px-1 rounded-md text-xs font-medium transition-colors',
                    selectedTime === slot?.value
                      ? 'bg-primary text-primary-foreground'
                      : slot?.available
                        ? 'bg-muted hover:bg-accent hover:text-accent-foreground text-foreground'
                        : 'bg-muted/40 text-muted-foreground cursor-not-allowed opacity-50'
                  )}
                >
                  {slot?.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirm */}
      {selectedDate && selectedTime && (
        <div className="bg-card border border-border rounded-lg px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Créneau sélectionné</p>
            <p className="text-sm font-semibold text-foreground capitalize">
              {availableDates?.find(d => d?.value === selectedDate)?.label} à {selectedTime}
            </p>
          </div>
          <Button
            variant="default"
            size="default"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={handleConfirmDateTime}
          >
            Confirmer ce créneau
          </Button>
        </div>
      )}
    </div>
  );
};

export default BookingCalendar;